import React, { useState } from "react";
import { TermsAndConditionsSection } from "./styles";
import { Box, Container, Divider, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";

type Lang = "EN" | "AR";

const TermsAndConditions = () => {
	const [lang, setLang] = useState<Lang>("AR");
	const isAr = lang === "AR";

	return (
		<Box pt={4}>
			<TermsAndConditionsSection dir={isAr ? "rtl" : "ltr"}>
				<Container maxWidth={"md"}>
					<Box display={"flex"} justifyContent={"flex-end"} mb={2}>
						<ToggleButtonGroup
							size={"small"}
							value={lang}
							exclusive
							onChange={(_, value) => value && setLang(value)}
							aria-label={"language toggle"}
							style={{ direction: "ltr" }}
						>
							<ToggleButton value={"EN"}>EN</ToggleButton>
							<ToggleButton value={"AR"}>AR</ToggleButton>
						</ToggleButtonGroup>
					</Box>
					<Box display={"flex"} flexDirection={"column"}>
						<Typography variant={"h2"} className={"page-title"}>
							{isAr ? "الشروط والأحكام" : "Terms & Conditions"}
						</Typography>
						<Typography className={"effective-date"}>
							{isAr ? "تاريخ السريان: 14 يونيو 2026" : "Effective Date: June 14, 2026"}
						</Typography>

						<Typography className={"policy-intro"}>
							{isAr ? (
								<>
									تحكم هذه الشروط والأحكام ("الشروط") استخدامك لمنصة <strong>VitesLink</strong> ("نحن" أو "لنا")
									وخدماتنا الرقمية لإنشاء الدعوات وإدارتها. من خلال الوصول إلى المنصة أو استخدامها، فإنك توافق على
									الالتزام بهذه الشروط. إذا كنت لا توافق عليها، يُرجى عدم استخدام الخدمة.
								</>
							) : (
								<>
									These Terms & Conditions ("Terms") govern your use of the <strong>VitesLink</strong> ("we", "our",
									or "us") platform and our digital invitation creation and management services. By accessing or
									using the platform, you agree to be bound by these Terms. If you do not agree, please do not use
									the service.
								</>
							)}
						</Typography>

						<Divider sx={{ mb: 4 }} />

						{/* Section 1 */}
						<Box className={"section"}>
							<Typography className={"section-title"}>
								{isAr ? "1. القبول بالشروط" : "1. Acceptance of Terms"}
							</Typography>
							<Typography className={"section-body"}>
								{isAr
									? "باستخدامك VitesLink، فإنك تقر بأنك قرأت هذه الشروط وفهمتها ووافقت على الالتزام بها، بالإضافة إلى سياسة الخصوصية وسياسة الاسترداد والإلغاء الخاصة بنا."
									: "By using VitesLink, you acknowledge that you have read, understood, and agree to be bound by these Terms, as well as our Privacy Policy and Refund & Cancellation Policy."}
							</Typography>
						</Box>

						{/* Section 2 */}
						<Box className={"section"}>
							<Typography className={"section-title"}>
								{isAr ? "2. وصف الخدمة" : "2. Description of Service"}
							</Typography>
							<Typography className={"section-body"}>
								{isAr
									? "توفّر VitesLink منصة رقمية لإنشاء دعوات الزفاف وإدارتها ومشاركتها، بما في ذلك قوالب قابلة للتخصيص، ووظائف تأكيد الحضور (RSVP)، ومعرض للصور، والعد التنازلي، وميزات أخرى ذات صلة. نحتفظ بالحق في تعديل أو إضافة أو إزالة أي ميزة في أي وقت دون إشعار مسبق."
									: "VitesLink provides a digital platform for creating, managing, and sharing wedding invitations, including customizable templates, RSVP functionality, photo galleries, countdowns, and related features. We reserve the right to modify, add, or remove any feature at any time without prior notice."}
							</Typography>
						</Box>

						{/* Section 3 */}
						<Box className={"section"}>
							<Typography className={"section-title"}>{isAr ? "3. الحسابات" : "3. Accounts"}</Typography>
							<Typography className={"section-body"}>
								{isAr ? "لاستخدام معظم ميزات المنصة، يجب عليك إنشاء حساب. أنت توافق على:" : "To use most platform features, you must create an account. You agree to:"}
							</Typography>
							<ul>
								{isAr ? (
									<>
										<li>تقديم معلومات دقيقة وحديثة عند التسجيل.</li>
										<li>الحفاظ على سرية بيانات تسجيل الدخول الخاصة بك.</li>
										<li>تحمّل المسؤولية الكاملة عن جميع الأنشطة التي تتم عبر حسابك.</li>
										<li>إخطارنا فورًا في حال الاشتباه بأي استخدام غير مصرح به لحسابك.</li>
									</>
								) : (
									<>
										<li>Provide accurate and up-to-date information when registering.</li>
										<li>Keep your login credentials confidential.</li>
										<li>Take full responsibility for all activity that occurs under your account.</li>
										<li>Notify us immediately if you suspect any unauthorized use of your account.</li>
									</>
								)}
							</ul>
							<Typography className={"section-body"} mt={1}>
								{isAr
									? "يجب أن يكون عمرك 18 عامًا على الأقل لاستخدام VitesLink."
									: "You must be at least 18 years old to use VitesLink."}
							</Typography>
						</Box>

						{/* Section 4 */}
						<Box className={"section"}>
							<Typography className={"section-title"}>
								{isAr ? "4. محتوى المستخدم ومسؤولياته" : "4. User Content & Responsibilities"}
							</Typography>
							<Typography className={"section-body"}>
								{isAr
									? "أنت المسؤول الوحيد عن جميع المحتويات التي تقوم برفعها أو إدخالها على المنصة، بما في ذلك النصوص والصور وأسماء الضيوف وأي وسائط أخرى (“المحتوى”). من خلال رفع المحتوى، فإنك تقر بما يلي:"
									: "You are solely responsible for all content you upload or enter into the platform, including text, photos, guest names, and any other media (“Content”). By uploading Content, you confirm that:"}
							</Typography>
							<ul>
								{isAr ? (
									<>
										<li>أنت تملك الحقوق اللازمة لاستخدام هذا المحتوى ونشره.</li>
										<li>المحتوى لا ينتهك حقوق أي طرف ثالث، بما في ذلك حقوق الملكية الفكرية أو الخصوصية.</li>
										<li>المحتوى لا يحتوي على مواد غير قانونية أو مسيئة أو مضللة.</li>
										<li>حصلت على موافقة أي ضيوف قمت بمشاركة بياناتهم عبر ميزات تأكيد الحضور (RSVP).</li>
									</>
								) : (
									<>
										<li>You own the necessary rights to use and publish this Content.</li>
										<li>The Content does not infringe any third party's rights, including intellectual property or privacy rights.</li>
										<li>The Content does not contain unlawful, abusive, or misleading material.</li>
										<li>You have obtained consent from any guests whose data you share via RSVP features.</li>
									</>
								)}
							</ul>
							<Typography className={"section-body"} mt={1}>
								{isAr
									? "نحتفظ بالحق في إزالة أي محتوى ننفذه اتفق أنه ينتهك هذه الشروط أو القوانين المعمول بها، دون إشعار مسبق."
									: "We reserve the right to remove any Content we determine, in our sole discretion, violates these Terms or applicable law, without prior notice."}
							</Typography>
						</Box>

						{/* Section 5 */}
						<Box className={"section"}>
							<Typography className={"section-title"}>
								{isAr ? "5. الدفع والتسعير" : "5. Payments & Pricing"}
							</Typography>
							<Typography className={"section-body"}>
								{isAr ? (
									<>
										بعض الميزات والخطط على VitesLink تتطلب دفعًا. تتم معالجة جميع المدفوعات بأمان عبر مزودي دفع
										خارجيين موثوقين. باستخدامك خدمات الدفع لدينا، فإنك توافق على أسعار الخطة المعروضة وقت الشراء.
										<br />
										<br />
										تخضع طلبات الاسترداد والإلغاء لـ <strong>سياسة الاسترداد والإلغاء</strong> الخاصة بنا.
									</>
								) : (
									<>
										Certain features and plans on VitesLink require payment. All payments are processed securely
										through trusted third-party payment providers. By using our paid services, you agree to the plan
										pricing displayed at the time of purchase.
										<br />
										<br />
										Refund and cancellation requests are subject to our <strong>Refund & Cancellation Policy</strong>.
									</>
								)}
							</Typography>
						</Box>

						{/* Section 6 */}
						<Box className={"section"}>
							<Typography className={"section-title"}>
								{isAr ? "6. الملكية الفكرية" : "6. Intellectual Property"}
							</Typography>
							<Typography className={"section-body"}>
								{isAr
									? "تبقى جميع حقوق الملكية الفكرية المتعلقة بمنصة VitesLink، بما في ذلك القوالب والتصميمات والشعارات والكود البرمجي، ملكًا حصريًا لنا أو للجهات المرخصة لنا. لا يُسمح لك بنسخ أو تعديل أو توزيع أو إعادة بيع أي جزء من المنصة دون إذن كتابي مسبق منا."
									: "All intellectual property rights related to the VitesLink platform, including templates, designs, logos, and source code, remain the exclusive property of VitesLink or our licensors. You may not copy, modify, distribute, or resell any part of the platform without our prior written permission."}
							</Typography>
							<Typography className={"section-body"} mt={1}>
								{isAr
									? "أنت تحتفظ بجميع الحقوق المتعلقة بالمحتوى الذي تقوم برفعه، وتمنحنا ترخيصًا محدودًا لاستضافة هذا المحتوى وعرضه فقط بغرض تقديم الخدمة."
									: "You retain all rights to the Content you upload, and you grant us a limited license to host and display that Content solely for the purpose of providing the service."}
							</Typography>
						</Box>

						{/* Section 7 */}
						<Box className={"section"}>
							<Typography className={"section-title"}>{isAr ? "7. الاستخدامات المحظورة" : "7. Prohibited Uses"}</Typography>
							<Typography className={"section-body"}>
								{isAr ? "أنت توافق على عدم استخدام VitesLink من أجل:" : "You agree not to use VitesLink to:"}
							</Typography>
							<ul>
								{isAr ? (
									<>
										<li>انتهاك أي قانون أو لائحة معمول بها.</li>
										<li>نشر محتوى مسيء أو تمييزي أو مضلل أو احتيالي.</li>
										<li>محاولة الوصول غير المصرح به إلى أنظمتنا أو حسابات مستخدمين آخرين.</li>
										<li>تعطيل أو إثقال البنية التحتية للمنصة (مثل هجمات الحرمان من الخدمة).</li>
										<li>استخدام أدوات آلية لكشط أو نسخ محتوى المنصة دون إذن.</li>
										<li>انتحال شخصية أي فرد أو جهة أخرى.</li>
									</>
								) : (
									<>
										<li>Violate any applicable law or regulation.</li>
										<li>Post abusive, discriminatory, misleading, or fraudulent content.</li>
										<li>Attempt unauthorized access to our systems or other users' accounts.</li>
										<li>Disrupt or overload the platform's infrastructure (e.g. denial-of-service attacks).</li>
										<li>Use automated tools to scrape or copy platform content without permission.</li>
										<li>Impersonate any person or entity.</li>
									</>
								)}
							</ul>
							<Typography className={"section-body"} mt={1}>
								{isAr
									? "يجوز لنا تعليق أو إنهاء حسابك فورًا إذا خالفت أيًا مما سبق."
									: "We may suspend or terminate your account immediately if you violate any of the above."}
							</Typography>
						</Box>

						{/* Section 8 */}
						<Box className={"section"}>
							<Typography className={"section-title"}>
								{isAr ? "8. خدمات الطرف الثالث" : "8. Third-Party Services"}
							</Typography>
							<Typography className={"section-body"}>
								{isAr
									? "تعتمد المنصة على خدمات طرف ثالث مثل مزودي الدفع، وخرائط جوجل، وGoogle Analytics، وMeta Pixel. لا نتحمل مسؤولية أي أعطال أو مشاكل ناتجة عن هذه الخدمات الخارجية، وتخضع لشروط وسياسات مزوديها الخاصة."
									: "The platform relies on third-party services such as payment providers, Google Maps, Google Analytics, and Meta Pixel. We are not responsible for any failures or issues caused by these external services, which are subject to their respective providers' terms and policies."}
							</Typography>
						</Box>

						{/* Section 9 */}
						<Box className={"section"}>
							<Typography className={"section-title"}>{isAr ? "9. الإنهاء" : "9. Termination"}</Typography>
							<Typography className={"section-body"}>
								{isAr
									? "يجوز لنا تعليق أو إنهاء وصولك إلى المنصة في أي وقت، بإشعار أو بدونه، إذا اعتقدنا أنك انتهكت هذه الشروط أو تسببت في ضرر للمنصة أو مستخدمين آخرين. يمكنك أيضًا إغلاق حسابك في أي وقت من خلال إعدادات حسابك أو بالتواصل مع الدعم."
									: "We may suspend or terminate your access to the platform at any time, with or without notice, if we believe you have violated these Terms or caused harm to the platform or other users. You may also close your account at any time through your account settings or by contacting support."}
							</Typography>
						</Box>

						{/* Section 10 */}
						<Box className={"section"}>
							<Typography className={"section-title"}>
								{isAr ? "10. إخلاء المسؤولية عن الضمانات" : "10. Disclaimer of Warranties"}
							</Typography>
							<Typography className={"section-body"}>
								{isAr
									? 'يتم تقديم المنصة "كما هي" و"حسب توفرها" دون أي ضمانات من أي نوع، صريحة أو ضمنية. لا نضمن أن الخدمة ستكون خالية من الأخطاء أو الانقطاعات، ولا نضمن نتائج محددة من استخدام المنصة.'
									: 'The platform is provided "as is" and "as available" without warranties of any kind, express or implied. We do not guarantee that the service will be error-free or uninterrupted, nor do we guarantee specific results from using the platform.'}
							</Typography>
						</Box>

						{/* Section 11 */}
						<Box className={"section"}>
							<Typography className={"section-title"}>
								{isAr ? "11. تحديد المسؤولية" : "11. Limitation of Liability"}
							</Typography>
							<Typography className={"section-body"}>
								{isAr
									? "إلى أقصى حد يسمح به القانون، لا تتحمل VitesLink مسؤولية أي أضرار غير مباشرة أو عرضية أو خاصة أو تبعية ناتجة عن استخدامك للمنصة أو عدم قدرتك على استخدامها، بما في ذلك على سبيل المثال لا الحصر فقدان البيانات أو الأرباح."
									: "To the maximum extent permitted by law, VitesLink shall not be liable for any indirect, incidental, special, or consequential damages arising from your use of, or inability to use, the platform, including but not limited to loss of data or profits."}
							</Typography>
						</Box>

						{/* Section 12 */}
						<Box className={"section"}>
							<Typography className={"section-title"}>
								{isAr ? "12. القانون الحاكم" : "12. Governing Law"}
							</Typography>
							<Typography className={"section-body"}>
								{isAr
									? "تخضع هذه الشروط وتُفسَّر وفقًا للقوانين المعمول بها في النطاق القضائي الذي تأسست فيه VitesLink قانونيًا، دون اعتبار لتعارض مبادئ القوانين. تخضع أي نزاعات ناشئة عن هذه الشروط للاختصاص القضائي الحصري لمحاكم ذلك النطاق."
									: "These Terms shall be governed by and construed in accordance with the laws applicable in the jurisdiction in which VitesLink is legally incorporated, without regard to conflict of law principles. Any disputes arising from these Terms shall be subject to the exclusive jurisdiction of the courts of that jurisdiction."}
							</Typography>
							<Typography className={"section-body"} mt={1}>
								{isAr
									? "إذا كنت مستهلكًا مقيمًا في نطاق قضائي يمنحك حقوقًا قانونية إلزامية لا يمكن تقييدها بموجب عقد، فإن هذا البند لا يُلغي تلك الحقوق."
									: "If you are a consumer residing in a jurisdiction that grants you mandatory statutory protections which cannot be limited by contract, this clause does not override those protections."}
							</Typography>
						</Box>

						{/* Section 13 */}
						<Box className={"section"}>
							<Typography className={"section-title"}>
								{isAr ? "13. تعديلات على هذه الشروط" : "13. Changes to These Terms"}
							</Typography>
							<Typography className={"section-body"}>
								{isAr
									? "قد نقوم بتحديث هذه الشروط من وقت لآخر. سيتم نشر التعديلات على هذه الصفحة مع تحديث تاريخ السريان. استمرارك في استخدام المنصة بعد أي تحديث يُعد موافقة منك على الشروط المعدّلة."
									: "We may update these Terms from time to time. Changes will be posted on this page with an updated effective date. Continued use of the platform after any update constitutes your acceptance of the revised Terms."}
							</Typography>
						</Box>

						{/* Section 14 */}
						<Box className={"section"}>
							<Typography className={"section-title"}>{isAr ? "14. تواصل معنا" : "14. Contact Us"}</Typography>
							<Typography className={"section-body"} mb={1}>
								{isAr
									? "إذا كانت لديك أي أسئلة حول هذه الشروط والأحكام، يُرجى التواصل مع فريق الدعم لدينا:"
									: "If you have any questions about these Terms & Conditions, please contact our support team:"}
							</Typography>
							<Box className={"contact-box"}>
								<Typography fontSize={"14px"} color={"#555"}>
									{isAr ? "البريد الإلكتروني:" : "Email:"} <a href="mailto:info@viteslink.com">info@viteslink.com</a>
								</Typography>
							</Box>
						</Box>
					</Box>
				</Container>
			</TermsAndConditionsSection>
		</Box>
	);
};

export default TermsAndConditions;
